<template>
  <div class="sales-report">
    <div class="report-header radius-7">
      <div>
        <h5 class="mb-1 fw-bold">Relatórios de vendas</h5>
        <p class="mb-0 text-muted fs-14">Acompanhe faturamento, produtos, vendedores e projeção mensal.</p>
      </div>
      <input
        v-model="selectedMonth"
        type="month"
        class="form-control report-month-input radius-7"
        @change="loadReport"
      />
    </div>

    <div v-if="isLoading" class="report-loading radius-7">
      Carregando relatório...
    </div>

    <template v-else>
      <section class="report-cards">
        <div class="report-card radius-7">
          <span>Total vendido</span>
          <strong>{{ money(summary.total_sold) }}</strong>
          <small :class="growthClass">{{ growthLabel }}</small>
        </div>
        <div class="report-card radius-7">
          <span>Vendas concluídas</span>
          <strong>{{ summary.completed_sales_count || 0 }}</strong>
          <small>{{ summary.canceled_sales_count || 0 }} canceladas</small>
        </div>
        <div class="report-card radius-7">
          <span>Ticket médio</span>
          <strong>{{ money(summary.average_ticket) }}</strong>
          <small>Por venda concluída</small>
        </div>
        <div class="report-card radius-7">
          <span>Produtos vendidos</span>
          <strong>{{ summary.products_sold_count || 0 }}</strong>
          <small>Itens movimentados</small>
        </div>
        <div class="report-card radius-7 report-card--highlight">
          <span>Projeção do mês</span>
          <strong>{{ money(summary.projected_total) }}</strong>
          <small>Baseada no ritmo atual</small>
        </div>
      </section>

      <section class="report-grid">
        <div class="report-panel radius-7">
          <div class="report-panel-header">
            <h6>Vendas por dia</h6>
            <span>{{ dailySales.length }} dias com venda</span>
          </div>
          <div v-if="dailySales.length > 0" class="daily-chart">
            <div v-for="day in dailySales" :key="day.date" class="daily-bar-row">
              <span>{{ formatDay(day.date) }}</span>
              <div class="daily-bar-track">
                <div class="daily-bar" :style="{ width: `${barWidth(day.total, maxDailyTotal)}%` }"></div>
              </div>
              <strong>{{ money(day.total) }}</strong>
            </div>
          </div>
          <div v-else class="report-empty">Nenhuma venda concluída neste mês.</div>
        </div>

        <div class="report-panel radius-7">
          <div class="report-panel-header">
            <h6>Produtos mais vendidos</h6>
            <span>Top {{ topProducts.length }}</span>
          </div>
          <div v-if="topProducts.length > 0" class="ranking-list">
            <div v-for="product in topProducts" :key="product.product_id" class="ranking-item">
              <div>
                <strong>{{ product.product_name }}</strong>
                <small>{{ product.sku || '-' }} &bull; {{ product.quantity }} un.</small>
              </div>
              <span>{{ money(product.total) }}</span>
            </div>
          </div>
          <div v-else class="report-empty">Nenhum produto vendido neste mês.</div>
        </div>

        <div class="report-panel radius-7">
          <div class="report-panel-header">
            <h6>Ranking de vendedores</h6>
            <span>Por faturamento</span>
          </div>
          <div v-if="sellerRanking.length > 0" class="ranking-list">
            <div v-for="seller in sellerRanking" :key="seller.user_id" class="ranking-item">
              <div>
                <strong>{{ seller.user_name }}</strong>
                <small>{{ seller.sales_count }} vendas</small>
              </div>
              <span>{{ money(seller.total) }}</span>
            </div>
          </div>
          <div v-else class="report-empty">Nenhum vendedor com venda neste mês.</div>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import api from '../../config/axios.js';

type ReportSummary = {
  total_sold?: number;
  completed_sales_count?: number;
  canceled_sales_count?: number;
  products_sold_count?: number;
  average_ticket?: number;
  projected_total?: number;
  growth_percent?: number | null;
};

type DailySale = {
  date: string;
  sales_count: number;
  total: number;
};

type TopProduct = {
  product_id: number;
  product_name: string;
  sku?: string;
  quantity: number;
  total: number;
};

type SellerRanking = {
  user_id: number;
  user_name: string;
  sales_count: number;
  total: number;
};

export default defineComponent({
  name: "SalesReportsTab",
  data() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    return {
      selectedMonth: `${now.getFullYear()}-${month}`,
      isLoading: false,
      summary: {} as ReportSummary,
      dailySales: [] as DailySale[],
      topProducts: [] as TopProduct[],
      sellerRanking: [] as SellerRanking[],
    };
  },
  computed: {
    maxDailyTotal(): number {
      return Math.max(...this.dailySales.map((day) => Number(day.total || 0)), 0);
    },
    growthLabel(): string {
      if (this.summary.growth_percent === null || this.summary.growth_percent === undefined) {
        return 'Sem comparação anterior';
      }

      const value = Number(this.summary.growth_percent);
      return `${value >= 0 ? '+' : ''}${value.toFixed(2).replace('.', ',')}% vs. mês anterior`;
    },
    growthClass(): string {
      if (this.summary.growth_percent === null || this.summary.growth_percent === undefined) {
        return '';
      }

      return Number(this.summary.growth_percent) >= 0 ? 'text-success' : 'text-danger';
    }
  },
  methods: {
    async loadReport() {
      const [year, month] = this.selectedMonth.split('-');
      this.isLoading = true;

      try {
        const response = await api.axios.get(`/api/sales/reports/${Number(month)}/${Number(year)}`);
        const payload = response.data || {};
        this.summary = payload.summary || {};
        this.dailySales = payload.daily_sales || [];
        this.topProducts = payload.top_products || [];
        this.sellerRanking = payload.seller_ranking || [];
      } catch (error) {
        this.summary = {};
        this.dailySales = [];
        this.topProducts = [];
        this.sellerRanking = [];
      } finally {
        this.isLoading = false;
      }
    },
    money(value?: number | string) {
      return `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;
    },
    formatDay(date: string) {
      const [, month, day] = String(date).split('-');
      return `${day}/${month}`;
    },
    barWidth(value: number | string, max: number) {
      if (!max || max <= 0) return 0;
      return Math.max(7, Math.round((Number(value || 0) / max) * 100));
    }
  },
  mounted() {
    this.loadReport();
  }
});
</script>

<style scoped>
.sales-report {
  display: grid;
  gap: 18px;
}

.report-header {
  align-items: center;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(47, 49, 82, 0.06);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 20px 24px;
}

.report-month-input {
  max-width: 190px;
}

.report-loading,
.report-empty {
  background: #ffffff;
  color: #6b7280;
  padding: 28px;
  text-align: center;
}

.report-cards {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.report-card {
  background: #ffffff;
  border: 1px solid #edf0f7;
  padding: 18px;
}

.report-card span,
.report-card small {
  color: #6b7280;
  display: block;
  font-size: 12px;
}

.report-card strong {
  color: #2f3152;
  display: block;
  font-size: 23px;
  margin: 7px 0 5px;
}

.report-card--highlight {
  background: #e8f8f3;
  border-color: #c8efe2;
}

.report-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: 1.35fr 1fr;
}

.report-panel {
  background: #ffffff;
  border: 1px solid #edf0f7;
  padding: 20px;
}

.report-panel:first-child {
  grid-row: span 2;
}

.report-panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.report-panel-header h6 {
  font-weight: 700;
  margin: 0;
}

.report-panel-header span {
  color: #6b7280;
  font-size: 12px;
}

.daily-chart {
  display: grid;
  gap: 12px;
}

.daily-bar-row {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 48px 1fr 92px;
}

.daily-bar-row span,
.daily-bar-row strong {
  color: #2f3152;
  font-size: 13px;
}

.daily-bar-track {
  background: #edf0f7;
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
}

.daily-bar {
  background: #00b894;
  border-radius: 999px;
  height: 100%;
}

.ranking-list {
  display: grid;
  gap: 12px;
}

.ranking-item {
  align-items: center;
  border-bottom: 1px solid #edf0f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding-bottom: 12px;
}

.ranking-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.ranking-item strong,
.ranking-item small {
  display: block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-item small {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

.ranking-item span {
  color: #2f3152;
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 1199.98px) {
  .report-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .report-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575.98px) {
  .report-header {
    align-items: stretch;
    flex-direction: column;
    padding: 18px;
  }

  .report-month-input {
    max-width: none;
  }

  .report-cards {
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .report-card {
    padding: 14px;
  }

  .report-card:first-child {
    grid-column: 1 / -1;
  }

  .report-card strong {
    font-size: 20px;
  }

  .report-card:first-child strong {
    font-size: 23px;
  }

  .daily-bar-row {
    grid-template-columns: 44px 1fr;
  }

  .daily-bar-row strong {
    grid-column: 2;
  }
}
</style>
